import React, { useEffect, useState } from 'react';
import { useSpamFilterToggle } from '../hooks/useLocalStorageToggle';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
  Alert,
  Grid,
  Checkbox,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Dialog,
  FormControlLabel,
  Switch,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Badge,
  Collapse
} from '@mui/material';
import { CheckCircle, Block, Close as CloseIcon, ExpandMore, ExpandLess } from '@mui/icons-material';
import contactService from '../services/contactService';
import { getSegmentColor } from '../utils/segmentColors';
import apiService from '../services/apiService';
import { useUser } from '../contexts/UserContext';
import { format, parseISO } from 'date-fns';
import EmailPreviewModal from './EmailPreviewModal';
import { obfuscateEmail } from '../utils/roleUtils';

interface ContactDetailModalProps {
  open: boolean;
  onClose: () => void;
  contactId: string | number;
  clientId: number;
  initialEditMode?: boolean;
  dateRange?: { startDate?: Date; endDate?: Date };
}

const ContactDetailModal: React.FC<ContactDetailModalProps> = ({
  open,
  onClose,
  contactId,
  clientId,
  initialEditMode = false,
  dateRange
}) => {
  const { isICRole } = useUser();
  const [contact, setContact] = useState<any>(null);
  const [client, setClient] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [allSegments, setAllSegments] = useState<any[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [editContact, setEditContact] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [bounces, setBounces] = useState<any[]>([]);
  const [complaints, setComplaints] = useState<any[]>([]);
  const [bouncesLoading, setBouncesLoading] = useState(false);
  const [complaintsLoading, setComplaintsLoading] = useState(false);
  const [emailEngagement, setEmailEngagement] = useState<any>(null);
  const [emailEngagementLoading, setEmailEngagementLoading] = useState(false);
  const [emailEngagementError, setEmailEngagementError] = useState<string | null>(null);
  const [includeSpamChecker, setIncludeSpamChecker] = useSpamFilterToggle();
  
  // Email preview modal state
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewEmail, setPreviewEmail] = useState<any>(null);
  const [loadingEmailDetail, setLoadingEmailDetail] = useState(false);

  // Collapsible sections state
  const [identityExpanded, setIdentityExpanded] = useState(true);
  const [dncExpanded, setDncExpanded] = useState(true);
  const [emailEngagementExpanded, setEmailEngagementExpanded] = useState(true);

  useEffect(() => {
    if (open && contactId) {
      fetchContact();
      if (initialEditMode) {
        setEditMode(true);
      }
    }
  }, [open, contactId, initialEditMode]);

  // Refetch email engagement when spam filter changes
  useEffect(() => {
    if (open && contactId && emailEngagement) {
      setEmailEngagementLoading(true);
      contactService.getContactEmailEngagement(
        contactId, 
        clientId, 
        !includeSpamChecker,
        dateRange?.startDate?.toISOString(),
        dateRange?.endDate?.toISOString()
      )
        .then((engagementData: any) => {
          setEmailEngagement(engagementData);
        })
        .catch((err: any) => {
          console.error('Email engagement fetch error:', err);
          setEmailEngagementError('Failed to load email engagement data.');
        })
        .finally(() => setEmailEngagementLoading(false));
    }
  }, [includeSpamChecker]);

  const fetchContact = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await contactService.getContact(contactId);
      setContact(res);
      setEditContact(res);
      
      // Fetch all segments for the client
      if (res.client_id) {
        const segs = await contactService.getSegments(res.client_id);
        setAllSegments(segs);
      }
      
      // Fetch client information to check VCRM sync status
      if (clientId) {
        try {
          const clientRes = await apiService.getClient(clientId);
          setClient(clientRes);
        } catch (err) {
          console.error('Error fetching client:', err);
        }
      }
      
      // Fetch bounces and complaints for this contact's email
      if (res.client_id && res.email_address) {
        setBouncesLoading(true);
        setComplaintsLoading(true);
        
        apiService.getBounces({ clientId: res.client_id, email_address: res.email_address, skip: 0, limit: 50 })
          .then((r: any) => {
            setBounces(r.data || []);
          })
          .catch((err: any) => {
            console.error('Bounce fetch error:', err);
          })
          .finally(() => setBouncesLoading(false));
        
        apiService.getComplaints({ clientId: res.client_id, email_address: res.email_address, skip: 0, limit: 50 })
          .then((r: any) => {
            setComplaints(r.data || []);
          })
          .catch((err: any) => {
            console.error('Complaint fetch error:', err);
          })
          .finally(() => setComplaintsLoading(false));
        
        // Fetch email engagement data for this contact
        setEmailEngagementLoading(true);
        contactService.getContactEmailEngagement(
          contactId, 
          res.client_id, 
          !includeSpamChecker,
          dateRange?.startDate?.toISOString(),
          dateRange?.endDate?.toISOString()
        )
          .then((engagementData: any) => {
            setEmailEngagement(engagementData);
          })
          .catch((err: any) => {
            console.error('Email engagement fetch error:', err);
            setEmailEngagementError('Failed to load email engagement data.');
          })
          .finally(() => setEmailEngagementLoading(false));
      }
    } catch (err: any) {
      setError('Failed to load contact.');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field: string, value: any) => {
    setEditContact((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleSegmentToggle = (segmentId: number) => {
    setEditContact((prev: any) => {
      const selectedIds = prev.segments ? prev.segments.map((s: any) => s.id) : [];
      const isSelected = selectedIds.includes(segmentId);
      let newSegments;
      if (isSelected) {
        newSegments = prev.segments.filter((s: any) => s.id !== segmentId);
      } else {
        const segObj = allSegments.find((s) => s.id === segmentId);
        newSegments = [...(prev.segments || []), segObj];
      }
      return { ...prev, segments: newSegments };
    });
  };

  const handleOk = async () => {
    if (!contact) return;
    setSaving(true);
    try {
      const payload: any = {
        ...editContact,
        segment_ids: (editContact.segments || []).map((s: any) => s.id),
      };
      // Remove only fields not allowed by backend
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.segments;
      // Ensure dnc_email is a boolean
      if (payload.dnc_email === undefined) {
        payload.dnc_email = false;
      }
      const updated = await contactService.updateContact(contact.id, payload);
      setContact(updated);
      setEditContact(updated);
      setEditMode(false);
      onClose(); // Close modal and return to contacts list
    } catch (err: any) {
      setError('Failed to update contact.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditContact(contact);
    setEditMode(false);
    onClose(); // Close modal and return to contacts list
  };

  const handleClose = () => {
    setEditMode(false);
    setEditContact(contact);
    onClose();
  };

  // Handle opening email preview modal
  const handleOpenEmailPreview = async (emailId: number) => {
    try {
      setLoadingEmailDetail(true);
      // Fetch full email details including HTML content
      const response = await apiService.get<any>(`/api/emails/${emailId}`);
      if (response.data) {
        setPreviewEmail(response.data);
        setPreviewModalOpen(true);
      }
    } catch (error) {
      console.error('Error fetching email details:', error);
      // Fallback to basic email info if API call fails
      setPreviewEmail({ id: emailId, name: 'Email', subject: 'No Subject' });
      setPreviewModalOpen(true);
    } finally {
      setLoadingEmailDetail(false);
    }
  };
  
  // Handle closing email preview modal
  const handleCloseEmailPreview = () => {
    setPreviewModalOpen(false);
    setPreviewEmail(null);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6">
              Contact Details
            </Typography>
            {contact?.vcrm_profile_id && (
              <Chip
                label="VCRM Synced"
                color="primary"
                size="small"
                variant="filled"
                clickable
                onClick={() => window.open(`https://vacationcrm.com/Profile/Edit/${contact.vcrm_profile_id}`, '_blank')}
                sx={{ 
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                    transform: 'scale(1.05)'
                  }
                }}
              />
            )}
          </Box>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : contact && editContact ? (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="h5">
                  {editContact.first_name || ''} {editContact.last_name || ''} {contact.email_address ? `(${isICRole() ? obfuscateEmail(contact.email_address) : contact.email_address})` : ''}
                </Typography>
                {editContact.vcrm_profile_id && client?.vcrm_sync && (
                  <Chip
                    label={`VCRM Sync (ID: ${editContact.vcrm_profile_id})`}
                    color="primary"
                    size="small"
                    variant="outlined"
                    clickable
                    onClick={() => window.open(`https://vacationcrm.com/Profile/Edit/${editContact.vcrm_profile_id}`, '_blank')}
                    sx={{ 
                      fontWeight: 'bold',
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'primary.main',
                        color: 'white'
                      }
                    }}
                  />
                )}
              </Box>
              {!editMode && !isICRole() && <Button variant="outlined" onClick={() => setEditMode(true)}>Edit</Button>}
            </Box>

            {/* Segments - moved above Identity section */}
            {loading || emailEngagementLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={2}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  Loading segment information...
                </Typography>
              </Box>
            ) : (
              <Box className="mb-4" display="flex" flexWrap="wrap" gap={1}>
                {editMode ? (
                  allSegments.length > 0 ? (
                    allSegments.map((seg) => {
                      const selected = (editContact.segments || []).some((s: any) => s.id === seg.id);
                      return (
                        <Chip
                          key={seg.id}
                          label={seg.name}
                          onClick={() => handleSegmentToggle(seg.id)}
                          style={{
                            backgroundColor: selected ? getSegmentColor(seg.name) : '#e0e0e0',
                            color: selected ? '#fff' : '#888',
                            opacity: selected ? 1 : 0.6,
                            cursor: 'pointer',
                          }}
                          variant={selected ? 'filled' : 'outlined'}
                        />
                      );
                    })
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No segments available for this client
                    </Typography>
                  )
                ) : (
                  (editContact.segments && editContact.segments.length > 0)
                    ? editContact.segments.map((seg: any) => (
                        <Chip
                          key={seg.id}
                          label={seg.name}
                          style={{
                            backgroundColor: getSegmentColor(seg.name),
                            color: '#fff',
                            opacity: 1,
                          }}
                          variant="filled"
                        />
                      ))
                    : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No segments assigned
                      </Typography>
                    )
                )}
              </Box>
            )}

            {(!editContact.vcrm_profile_id || !editMode) && (
              <Paper elevation={1} className="p-4 mb-4">
                <Box 
                  display="flex" 
                  alignItems="center" 
                  justifyContent="space-between" 
                  sx={{ cursor: 'pointer', mb: 2 }}
                  onClick={() => setIdentityExpanded(!identityExpanded)}
                >
                  <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 2 }}>Identity</Typography>
                  <IconButton size="small">
                    {identityExpanded ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>
                <Collapse in={identityExpanded}>
                {editContact.vcrm_profile_id && editMode && client?.vcrm_sync && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      This contact is synced with VacationCRM (
                      <a 
                        href={`https://vacationcrm.com/Profile/Edit/${editContact.vcrm_profile_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'inherit', textDecoration: 'underline' }}
                      >
                        Profile ID: {editContact.vcrm_profile_id}
                      </a>
                      ). 
                      <br />
                      <strong>Profile information</strong> (name, email, etc.) is managed in VacationCRM and cannot be edited here.
                      <br />
                      <strong>DNC settings</strong> can be updated below and will sync back to VacationCRM automatically.
                    </Typography>
                  </Alert>
                )}
                {loading ? (
                  <Grid container spacing={2}>
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <Grid item xs={12} sm={6} key={i}>
                        <Box sx={{ width: '100%', height: 40, bgcolor: 'grey.300', borderRadius: 1 }} />
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>First Name</Typography>
                      {editMode ? (
                        <TextField 
                          size="small" 
                          fullWidth 
                          value={editContact.first_name || ''} 
                          onChange={e => handleFieldChange('first_name', e.target.value)}
                          disabled={!!editContact.vcrm_profile_id}
                          helperText={editContact.vcrm_profile_id ? "Edit in VacationCRM" : ""}
                        />
                      ) : (
                        <Typography variant="body1">{editContact.first_name}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>Middle Name</Typography>
                      {editMode ? (
                        <TextField 
                          size="small" 
                          fullWidth 
                          value={editContact.middle_name || ''} 
                          onChange={e => handleFieldChange('middle_name', e.target.value)}
                          disabled={!!editContact.vcrm_profile_id}
                          helperText={editContact.vcrm_profile_id ? "Edit in VacationCRM" : ""}
                        />
                      ) : (
                        <Typography variant="body1">{editContact.middle_name}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>Last Name</Typography>
                      {editMode ? (
                        <TextField 
                          size="small" 
                          fullWidth 
                          value={editContact.last_name || ''} 
                          onChange={e => handleFieldChange('last_name', e.target.value)}
                          disabled={!!editContact.vcrm_profile_id}
                          helperText={editContact.vcrm_profile_id ? "Edit in VacationCRM" : ""}
                        />
                      ) : (
                        <Typography variant="body1">{editContact.last_name}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>Nick Name</Typography>
                      {editMode ? (
                        <TextField 
                          size="small" 
                          fullWidth 
                          value={editContact.nick_name || ''} 
                          onChange={e => handleFieldChange('nick_name', e.target.value)}
                          disabled={!!editContact.vcrm_profile_id}
                          helperText={editContact.vcrm_profile_id ? "Edit in VacationCRM" : ""}
                        />
                      ) : (
                        <Typography variant="body1">{editContact.nick_name}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>Gender</Typography>
                      {editMode ? (
                        <Select
                          size="small"
                          fullWidth
                          value={editContact.gender || ''}
                          onChange={e => handleFieldChange('gender', e.target.value)}
                          disabled={!!editContact.vcrm_profile_id}
                        >
                          <MenuItem value="">Select Gender</MenuItem>
                          <MenuItem value="male">Male</MenuItem>
                          <MenuItem value="female">Female</MenuItem>
                          <MenuItem value="other">Other</MenuItem>
                        </Select>
                      ) : (
                        <Typography variant="body1">{editContact.gender}</Typography>
                      )}
                      {editMode && editContact.vcrm_profile_id && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          Edit in VacationCRM
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>Birth Date</Typography>
                      {editMode ? (
                        <TextField 
                          size="small" 
                          fullWidth 
                          value={editContact.birth_date || ''} 
                          onChange={e => handleFieldChange('birth_date', e.target.value)}
                          disabled={!!editContact.vcrm_profile_id}
                          helperText={editContact.vcrm_profile_id ? "Edit in VacationCRM" : ""}
                        />
                      ) : (
                        <Typography variant="body1">{editContact.birth_date}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>Email Address</Typography>
                      {editMode ? (
                        <TextField 
                          size="small" 
                          fullWidth 
                          type="email"
                          value={editContact.email_address || ''} 
                          onChange={e => handleFieldChange('email_address', e.target.value)}
                          disabled={!!editContact.vcrm_profile_id}
                          helperText={editContact.vcrm_profile_id ? "Edit in VacationCRM" : ""}
                        />
                      ) : (
                        <Typography variant="body1">{isICRole() ? obfuscateEmail(editContact.email_address) : editContact.email_address}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>Mobile Phone</Typography>
                      {editMode ? (
                        <TextField 
                          size="small" 
                          fullWidth 
                          value={editContact.mobile_phone || ''} 
                          onChange={e => handleFieldChange('mobile_phone', e.target.value)}
                          disabled={!!editContact.vcrm_profile_id}
                          helperText={editContact.vcrm_profile_id ? "Edit in VacationCRM" : ""}
                        />
                      ) : (
                        <Typography variant="body1">{editContact.mobile_phone}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>Phone</Typography>
                      {editMode ? (
                        <TextField 
                          size="small" 
                          fullWidth 
                          value={editContact.phone || ''} 
                          onChange={e => handleFieldChange('phone', e.target.value)}
                          disabled={!!editContact.vcrm_profile_id}
                          helperText={editContact.vcrm_profile_id ? "Edit in VacationCRM" : ""}
                        />
                      ) : (
                        <Typography variant="body1">{editContact.phone}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>Title</Typography>
                      {editMode ? (
                        <TextField 
                          size="small" 
                          fullWidth 
                          value={editContact.title || ''} 
                          onChange={e => handleFieldChange('title', e.target.value)}
                          disabled={!!editContact.vcrm_profile_id}
                          helperText={editContact.vcrm_profile_id ? "Edit in VacationCRM" : ""}
                        />
                      ) : (
                        <Typography variant="body1">{editContact.title}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>Primary Agent</Typography>
                      {editMode ? (
                        <TextField 
                          size="small" 
                          fullWidth 
                          value={editContact.primary_agent || ''} 
                          onChange={e => handleFieldChange('primary_agent', e.target.value)}
                          disabled={!!editContact.vcrm_profile_id}
                          helperText={editContact.vcrm_profile_id ? "Edit in VacationCRM" : ""}
                        />
                      ) : (
                        <Typography variant="body1">{editContact.primary_agent}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>Address</Typography>
                      {editMode ? (
                        <TextField 
                          size="small" 
                          fullWidth 
                          multiline
                          rows={2}
                          value={editContact.address || ''} 
                          onChange={e => handleFieldChange('address', e.target.value)}
                          disabled={!!editContact.vcrm_profile_id}
                          helperText={editContact.vcrm_profile_id ? "Edit in VacationCRM" : ""}
                        />
                      ) : (
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{editContact.address}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>Address 2</Typography>
                      {editMode ? (
                        <TextField 
                          size="small" 
                          fullWidth 
                          multiline
                          rows={2}
                          value={editContact.address2 || ''} 
                          onChange={e => handleFieldChange('address2', e.target.value)}
                          disabled={!!editContact.vcrm_profile_id}
                          helperText={editContact.vcrm_profile_id ? "Edit in VacationCRM" : ""}
                        />
                      ) : (
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{editContact.address2}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>City</Typography>
                      {editMode ? (
                        <TextField 
                          size="small" 
                          fullWidth 
                          value={editContact.city || ''} 
                          onChange={e => handleFieldChange('city', e.target.value)}
                          disabled={!!editContact.vcrm_profile_id}
                          helperText={editContact.vcrm_profile_id ? "Edit in VacationCRM" : ""}
                        />
                      ) : (
                        <Typography variant="body1">{editContact.city}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>State</Typography>
                      {editMode ? (
                        <TextField 
                          size="small" 
                          fullWidth 
                          value={editContact.state || ''} 
                          onChange={e => handleFieldChange('state', e.target.value)}
                          disabled={!!editContact.vcrm_profile_id}
                          helperText={editContact.vcrm_profile_id ? "Edit in VacationCRM" : ""}
                        />
                      ) : (
                        <Typography variant="body1">{editContact.state}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>Zip Code</Typography>
                      {editMode ? (
                        <TextField 
                          size="small" 
                          fullWidth 
                          value={editContact.zip_code || ''} 
                          onChange={e => handleFieldChange('zip_code', e.target.value)}
                          disabled={!!editContact.vcrm_profile_id}
                          helperText={editContact.vcrm_profile_id ? "Edit in VacationCRM" : ""}
                        />
                      ) : (
                        <Typography variant="body1">{editContact.zip_code}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>Country</Typography>
                      {editMode ? (
                        <TextField 
                          size="small" 
                          fullWidth 
                          value={editContact.country || ''} 
                          onChange={e => handleFieldChange('country', e.target.value)}
                          disabled={!!editContact.vcrm_profile_id}
                          helperText={editContact.vcrm_profile_id ? "Edit in VacationCRM" : ""}
                        />
                      ) : (
                        <Typography variant="body1">{editContact.country}</Typography>
                      )}
                    </Grid>
                  </Grid>
                )}
                </Collapse>
              </Paper>
            )}

            {editContact.vcrm_profile_id && editMode && client?.vcrm_sync && (
              <Paper elevation={1} className="p-4 mb-4">
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    This contact is synced with VacationCRM (
                    <a 
                      href={`https://vacationcrm.com/Profile/Edit/${editContact.vcrm_profile_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'inherit', textDecoration: 'underline' }}
                    >
                      Profile ID: {editContact.vcrm_profile_id}
                    </a>
                    ). 
                    <br />
                    <strong>Profile information</strong> (name, email, etc.) is managed in VacationCRM and cannot be edited here.
                    <br />
                    <strong>DNC settings</strong> can be updated below and will sync back to VacationCRM automatically.
                  </Typography>
                </Alert>
              </Paper>
            )}

            {/* DNC Email Section - Always visible */}
            <Paper elevation={1} className="p-4 mb-4">
              <Box 
                display="flex" 
                alignItems="center" 
                justifyContent="space-between" 
                sx={{ cursor: 'pointer', mb: 2 }}
                onClick={() => setDncExpanded(!dncExpanded)}
              >
                <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 2 }}>Do Not Contact Settings</Typography>
                <IconButton size="small">
                  {dncExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              <Collapse in={dncExpanded}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>DNC Email</Typography>
                  {editMode ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Checkbox
                        checked={editContact.dnc_email || false}
                        onChange={e => handleFieldChange('dnc_email', e.target.checked)}
                        size="small"
                      />
                      <Typography variant="body2" color="text.secondary">
                        {editContact.dnc_email ? 'Do not contact via email' : 'Email contact allowed'}
                      </Typography>
                    </Box>
                  ) : (
                    <Box display="flex" alignItems="center" gap={1}>
                      {editContact.dnc_email ? (
                        <Block color="error" fontSize="small" />
                      ) : (
                        <CheckCircle color="success" fontSize="small" />
                      )}
                      <Typography variant="body1">
                        {editContact.dnc_email ? 'Do not contact via email' : 'Email contact allowed'}
                      </Typography>
                    </Box>
                  )}
                </Grid>
                {!editMode && editContact.dnc_email && editContact.dnc_email_note && (
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ 
                      p: 1.5, 
                      backgroundColor: 'grey.50', 
                      borderRadius: 1, 
                      border: '1px solid',
                      borderColor: 'grey.200',
                      whiteSpace: 'pre-wrap'
                    }}>
                      {editContact.dnc_email_date ? `${format(parseISO(editContact.dnc_email_date), 'MMM dd, yyyy HH:mm')}: ` : ''}{editContact.dnc_email_note}
                    </Typography>
                  </Grid>
                )}
                {editMode && editContact.dnc_email && (
                  <Grid item xs={12}>
                    <TextField 
                      fullWidth 
                      value={editContact.dnc_email_note || ''} 
                      onChange={e => handleFieldChange('dnc_email_note', e.target.value)}
                      placeholder="Reason for do not contact..."
                      multiline
                      rows={2}
                      sx={{ mt: 2.5 }}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>DNC SMS</Typography>
                  {editMode ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Checkbox
                        checked={editContact.dnc_sms || false}
                        onChange={e => handleFieldChange('dnc_sms', e.target.checked)}
                        size="small"
                      />
                      <Typography variant="body2" color="text.secondary">
                        {editContact.dnc_sms ? 'Do not contact via SMS' : 'SMS contact allowed'}
                      </Typography>
                    </Box>
                  ) : (
                    <Box display="flex" alignItems="center" gap={1}>
                      {editContact.dnc_sms ? (
                        <Block color="error" fontSize="small" />
                      ) : (
                        <CheckCircle color="success" fontSize="small" />
                      )}
                      <Typography variant="body1">
                        {editContact.dnc_sms ? 'Do not contact via SMS' : 'SMS contact allowed'}
                      </Typography>
                    </Box>
                  )}
                </Grid>
                {!editMode && editContact.dnc_sms && editContact.dnc_sms_note && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>DNC SMS Note</Typography>
                    <Typography variant="body2" sx={{ 
                      p: 1.5, 
                      backgroundColor: 'grey.50', 
                      borderRadius: 1, 
                      border: '1px solid',
                      borderColor: 'grey.200',
                      whiteSpace: 'pre-wrap'
                    }}>
                      {editContact.dnc_sms_note}
                    </Typography>
                  </Grid>
                )}
                {editMode && editContact.dnc_sms && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>DNC SMS Note</Typography>
                    <TextField 
                      fullWidth 
                      value={editContact.dnc_sms_note || ''} 
                      onChange={e => handleFieldChange('dnc_sms_note', e.target.value)}
                      placeholder="Reason for do not contact via SMS..."
                      multiline
                      rows={2}
                      sx={{ mt: 2.5 }}
                    />
                  </Grid>
                )}
              </Grid>
              </Collapse>
            </Paper>

            {/* Email Engagement */}
            {!editMode && (
              <Paper elevation={1} className="p-4 mb-4">
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box 
                    display="flex" 
                    alignItems="center" 
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setEmailEngagementExpanded(!emailEngagementExpanded)}
                  >
                    <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 2 }}>Email Engagement</Typography>
                    <IconButton size="small" sx={{ ml: 1 }}>
                      {emailEngagementExpanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Box>
                  {!emailEngagementLoading && (
                    <Tooltip 
                      title={
                        <Box>
                          <Typography variant="body2" color="white" sx={{ mb: 1 }}>
                            <strong>Filter out likely spam checker clicks</strong>
                          </Typography>
                          <Typography variant="body2" color="rgba(255, 255, 255, 0.8)">
                            When enabled, excludes opens and clicks from IP addresses flagged as spam checkers. 
                            This provides more accurate engagement metrics by filtering out automated bot activity.
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                      sx={{
                        backgroundColor: 'rgba(0, 0, 0, 0.95)',
                        color: 'white',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        '& .MuiTooltip-arrow': {
                          color: 'rgba(0, 0, 0, 0.95)',
                        },
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={includeSpamChecker}
                            onChange={(e) => setIncludeSpamChecker(e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Filter out likely spam checker clicks"
                        sx={{ 
                          '& .MuiFormControlLabel-label': {
                            fontSize: '0.875rem',
                            color: 'text.secondary'
                          }
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
                
                <Collapse in={emailEngagementExpanded}>
                {emailEngagementLoading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                      Loading email engagement data...
                    </Typography>
                  </Box>
                ) : emailEngagementError ? (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {emailEngagementError}
                  </Alert>
                ) : emailEngagement && emailEngagement.emails && emailEngagement.emails.length > 0 ? (
                <Box>
                  <Box display="flex" gap={2} mb={2} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Total Emails: {emailEngagement.total_emails}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Opens: {emailEngagement.total_opens}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Clicks: {emailEngagement.total_clicks}
                    </Typography>
                  </Box>
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 800 }}>
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ minWidth: 150 }}><strong>Email</strong></TableCell>
                          <TableCell sx={{ minWidth: 200 }}><strong>Subject</strong></TableCell>
                          <TableCell sx={{ minWidth: 100 }}><strong>Date Sent</strong></TableCell>
                          <TableCell sx={{ minWidth: 120 }}><strong>Segment</strong></TableCell>
                          <TableCell sx={{ minWidth: 80 }}><strong>Opens</strong></TableCell>
                          <TableCell sx={{ minWidth: 80 }}><strong>Clicks</strong></TableCell>
                          <TableCell sx={{ minWidth: 120 }}><strong>Last Activity</strong></TableCell>
                        </TableRow>
                        {emailEngagement.emails
                          .sort((a: any, b: any) => {
                            // Sort by date_sent in ascending order (oldest first)
                            if (!a.date_sent && !b.date_sent) return 0;
                            if (!a.date_sent) return 1;
                            if (!b.date_sent) return -1;
                            return new Date(a.date_sent).getTime() - new Date(b.date_sent).getTime();
                          })
                          .map((email: any) => (
                          <TableRow key={email.email_id}>
                            <TableCell sx={{ minWidth: 150 }}>
                              <Button
                                size="small"
                                variant="text"
                                color="primary"
                                onClick={() => handleOpenEmailPreview(email.email_id)}
                                sx={{ textAlign: 'left', justifyContent: 'flex-start' }}
                              >
                                {email.email_name?.trim() || 'N/A'}
                              </Button>
                            </TableCell>
                            <TableCell sx={{ minWidth: 200 }}>{email.email_subject}</TableCell>
                            <TableCell sx={{ minWidth: 100 }}>
                              {email.date_sent ? format(parseISO(email.date_sent), 'yyyy-MM-dd') : 'N/A'}
                            </TableCell>
                            <TableCell sx={{ minWidth: 120 }}>{email.segment_name || 'N/A'}</TableCell>
                            <TableCell sx={{ minWidth: 80 }}>
                              <Box display="flex" alignItems="center" gap={1}>
                                {email.is_read ? (
                                  <CheckCircle color="success" fontSize="small" />
                                ) : (
                                  <Box 
                                    sx={{ 
                                      width: 20, 
                                      height: 20, 
                                      borderRadius: '50%', 
                                      bgcolor: 'grey.400',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }} 
                                  />
                                )}
                                {email.open_count}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ minWidth: 80 }}>{email.click_count}</TableCell>
                            <TableCell sx={{ minWidth: 120 }}>
                              {email.last_opened ? (
                                <Typography variant="caption">
                                  Opened: {format(parseISO(email.last_opened), 'yyyy-MM-dd HH:mm')}
                                </Typography>
                              ) : email.last_click ? (
                                <Typography variant="caption">
                                  Clicked: {format(parseISO(email.last_click), 'yyyy-MM-dd HH:mm')}
                                </Typography>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  No activity
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 2 }}>
                    No email engagement data available
                  </Typography>
                )}
                </Collapse>
              </Paper>
            )}

            {/* Bounces */}
            {!editMode && bounces.length > 0 && (
              <Paper elevation={1} className="p-4 mb-4">
                <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 2, mb: 2 }}>Bounces</Typography>
                <Box sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>Date</strong></TableCell>
                        <TableCell><strong>Bounce Type</strong></TableCell>
                        <TableCell><strong>Type</strong></TableCell>
                        <TableCell><strong>Email</strong></TableCell>
                        <TableCell><strong>Subject</strong></TableCell>
                      </TableRow>
                      {bounces.map((bounce: any) => (
                        <TableRow key={bounce.id}>
                          <TableCell>{bounce.bounce_timestamp ? format(parseISO(bounce.bounce_timestamp), 'yyyy-MM-dd') : ''}</TableCell>
                          <TableCell>{bounce.bounce_type}</TableCell>
                          <TableCell>{bounce.bounce_sub_type}</TableCell>
                          <TableCell>{bounce.bounce_recipient_email}</TableCell>
                          <TableCell>{bounce.common_subject}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Paper>
            )}

            {/* Complaints */}
            {!editMode && complaints.length > 0 && (
              <Paper elevation={1} className="p-4 mb-4">
                <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 2, mb: 2 }}>Complaints</Typography>
                <Box>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>Date</strong></TableCell>
                        <TableCell><strong>Email</strong></TableCell>
                        <TableCell><strong>Subject</strong></TableCell>
                        <TableCell><strong>Feedback Type</strong></TableCell>
                        <TableCell><strong>Subtype</strong></TableCell>
                      </TableRow>
                      {complaints.map((complaint: any) => (
                        <TableRow key={complaint.id}>
                          <TableCell>{complaint.complaint_timestamp ? format(parseISO(complaint.complaint_timestamp), 'yyyy-MM-dd') : ''}</TableCell>
                          <TableCell>{complaint.email_to}</TableCell>
                          <TableCell>{complaint.common_subject}</TableCell>
                          <TableCell>{complaint.complaint_feedback_type}</TableCell>
                          <TableCell>{complaint.complaint_sub_type}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Paper>
            )}

            {editMode && (
              <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                <Button variant="contained" color="primary" onClick={handleOk} disabled={saving}>Ok</Button>
                <Button variant="outlined" onClick={handleCancel} disabled={saving}>Cancel</Button>
              </Box>
            )}
          </Box>
        ) : null}
      </DialogContent>
      
      {!editMode && (
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      )}
      
      {/* Email Preview Modal */}
      <EmailPreviewModal
        open={previewModalOpen}
        onClose={handleCloseEmailPreview}
        email={previewEmail}
        loading={loadingEmailDetail}
      />
    </Dialog>
  );
};

export default ContactDetailModal;
